import { DependencyList, useCallback } from 'react'
import { CancelToken } from 'axios'
import { ApiAccess } from 'features/session/types'
import {
  ApiGetWithToken,
	ApiDeleteWithToken,
	ApiGetFileWithToken,
	ApiPatchWithToken,
	ApiPostFileWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiFetchList,
	useApiPost,
	FileResponse
} from 'shared/api'
import {
	selfApi,
  userApi,
  userApiBy
} from 'interface/endpoints'
import {
	UserFormData,
	UserNameFormData,
	AIParameterFormData,
  UserResult,
  UserResultList,
} from './types'

export * from './types'

export const useSelfUser = (
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      return ApiGetWithToken<UserResult>(
        apiAccess,
        selfApi('/user'),
        cancelToken
      )
    },
		[]
	)
)

export const useListUser = (
	option: {
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: UserResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { partialLimit, onPreFetch, onFetched } = option
	return useApiFetchList<UserResult, UserResult[]>(
		useCallback(
			(apiAccess, cancelToken) => {
				if (onPreFetch) onPreFetch()
				return ApiGetWithToken<number>(
					apiAccess,
					userApi('/count'),
					cancelToken
				)
			},
			[onPreFetch]
		),
		useCallback(
			(apiAccess, cancelToken, offset, limit) => {
				const wrapper = async () => {
					const list = await ApiGetWithToken<UserResultList>(
						apiAccess,
						userApi('/list', { offset, limit }),
						cancelToken
					)
					return list?.list
				}
				return wrapper()
			},
			[]
		),
		useCallback(
			(result) => {
				if (onFetched) onFetched(result)
				return result
			},
			[onFetched]
		),
		partialLimit ?? 1000,
		deps
	)
}

export const useGetSelfUser = (
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      return ApiGetWithToken<UserResult>(
        apiAccess,
        selfApi('/user'),
        cancelToken
  		)
    },
		[]
	)
)

export const useGetUser = (
  userId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      if (!userId) return null
      return ApiGetWithToken<UserResult>(
        apiAccess,
        userApiBy(userId),
        cancelToken
  		)
    },
		[userId]
	)
)

const postUserIcon = (
	apiAccess: ApiAccess,
	userId: number,
	file: File, 
	cancelToken?: CancelToken,
	webSocketId?: string
) => {
	const formData = new FormData()
	formData.append('file', file)
	return ApiPostFileWithToken(
		apiAccess,
		userApiBy(userId, '/icon'),
		formData,
		cancelToken,
		webSocketId
	)
}
export const useEditUser = (
  userId: number, 
	callback?: {
    onEditted?: (result: UserResult, icon?: FileResponse) => void
		onError?: (reason: Error | undefined) => void
  }
) => useApiPost<{ form: UserFormData, icon?: File }, UserResult>(
	useCallback(
		(apiAccess, cancelToken, { form }, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        userApiBy(userId),
				form,
				cancelToken,
				webSocketId
			)
		},
		[userId]
	),
	useCallback(
		async (result, { icon }, apiAccess, cancelToken) => {
			if (icon) {
				const file = await postUserIcon(apiAccess, result.userId, icon, cancelToken)
				if (callback?.onEditted) callback.onEditted(result, file)
			} else {
				if (callback?.onEditted) callback.onEditted(result)
			}
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useDeleteUser = (
  callback?: {
    onDeleted?: (userId: number) => void
		onError?: (reason: Error | undefined) => void
  }
) => useApiPost<number, number>(
	useCallback(
		(apiAccess, cancelToken, userId, webSocketId) => {
			return ApiDeleteWithToken(
				apiAccess,
        userApiBy(userId),
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onDeleted) callback.onDeleted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useResetPassword = (
  callback?: {
    onResetted?: () => void
		onError?: (reason: Error | undefined) => void
  }
) => useApiPost<number, UserResult>(
	useCallback(
		(apiAccess, cancelToken, userId, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        userApiBy(userId, '/passwordReset'),
				undefined,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onResetted) callback.onResetted()
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useUnlockUser = (
  callback?: {
    onUnlocked?: (unlockedId: number) => void
		onError?: (reason: Error | undefined) => void
  }
) => useApiPost<number, number>(
	useCallback(
		(apiAccess, cancelToken, userId, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        userApiBy(userId, '/unlock'),
				undefined,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onUnlocked) callback.onUnlocked(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditSelfUserName = (
  callback?: {
    onEditted?: (result: UserResult) => void
		onError?: (reason: Error | undefined) => void
  }
) => useApiPost<UserNameFormData, UserResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        selfApi('/user/name'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditSelfAIParam = (
  callback?: {
    onEditted?: (result: UserResult) => void
		onError?: (reason: Error | undefined) => void
  }
) => useApiPost<AIParameterFormData, UserResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        selfApi('/user/aiparam'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useGetUserIcon = (
	userId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
			if (!userId) return null
			return ApiGetFileWithToken(
				apiAccess,
				userApiBy(userId, '/icon'),
				cancelToken
			)
		},
		[userId]
	)
)

export const useGetSelfUserIcon = (
	deps: DependencyList = []
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
			return ApiGetFileWithToken(
				apiAccess,
				selfApi('/user/icon'),
				cancelToken
			)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		deps
	)
)

export const useSetSelfUserIcon = (
  callback?: {
    onPosted?: (result: FileResponse) => void
		onError?: (reason: Error | undefined) => void
  }
) => useApiPost<File, FileResponse>(
	useCallback(
		(apiAccess, cancelToken, file, webSocketId) => {
			const formData = new FormData()
			formData.append('file', file)
			return ApiPostFileWithToken(
				apiAccess,
        selfApi('/user/icon'),
				formData,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const GetSelfUserProfile = (
	apiAccess: ApiAccess,
	profileKey: string,
	cancelToken?: CancelToken
// eslint-disable-next-line @typescript-eslint/no-explicit-any
) => ApiGetWithToken<any>(
	apiAccess,
	selfApi(`/user/profile/${profileKey}`),
	cancelToken
)
export const useGetSelfUserProfile = (
	profileKey: string | undefined | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
			if (!profileKey) return null
			return GetSelfUserProfile(apiAccess, profileKey, cancelToken)
		},
		[profileKey]
	)
)

export const PostSelfUserProfile = (
  apiAccess: ApiAccess,
  profileKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profileData: any,
  cancelToken?: CancelToken,
	webSocketId?: string
) => ApiPostWithToken<FileResponse>(
	apiAccess,
	selfApi(`/user/profile/${profileKey}`),
	profileData,
	cancelToken,
	webSocketId
)

export const useSetSelfUserProfile = (
	profileKey: string,
  callback?: {
    onPosted?: (result: FileResponse) => void
		onError?: (reason: Error | undefined) => void
  }
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
) => useApiPost<any, FileResponse>(
	useCallback(
		(apiAccess, cancelToken, profileData, webSocketId) => {
			return PostSelfUserProfile(apiAccess, profileKey, profileData, cancelToken, webSocketId)
		},
		[profileKey]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useExtendUser = (
	callback?: {
		onPosted?: (result: UserResult) => void
		onError?: (reason: Error | undefined) => void
	}
) => useApiPost<number, UserResult>(
	useCallback(
		(apiAccess, cancelToken, userId, webSocketId) => {
			return ApiPostWithToken<UserResult>(
				apiAccess,
				userApiBy(userId, "/extend"),
				undefined,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
			if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)